import React, { useState } from 'react';
import BytesTutorials from '@components/shared/bytes/bytes-tutorials';
import BytesArticles from '@components/shared/bytes/BytesArticles';
import Link from 'next/link';
import { BsArrowRight } from 'react-icons/bs';
import { getArticles, getBytesStrapiUrl } from '@lib/bytesStrapiServices';
import axios from 'axios';
import BytesFooter from '@components/shared/bytes/bytes-footer/BytesFooter';
import SiteHeader from '@components/shared/SiteHeader';
import { FiArrowUpRight } from 'react-icons/fi';
import Marquee from 'react-fast-marquee';
import { getUtmQueryString } from '@lib/getUtmQueryString';
import { useRouter } from 'next/router';
import EventBanner from '@components/shared/bytes/EventBanner';

const cardData = [
  {
    title: (
      <p>
        Read
        <br />
        Articles
      </p>
    ),
    description:
      'Articles are an in-depth explanations of concepts and technologies developers use on a daily basis.',
    link: 'articles',
  },
  {
    title: (
      <p>
        Free
        <br />
        Tutorials
      </p>
    ),
    description:
      'Master fundamental data science and software engineering with AI concepts with free short tutorials.',
    link: 'tutorials',
  },
  {
    title: (
      <p>
        Watch
        <br />
        Videos
      </p>
    ),
    description:
      'Watch and learn from our collection of top videos that simplify complex data science and engineering topics.',
    link: 'https://www.youtube.com/@AlmaBetter/videos',
  },
  {
    title: (
      <p>
        Free
        <br />
        Quizzes
      </p>
    ),
    description: 'Challenge yourself on mind bending and exciting quizzes to level up your skills.',
    link: 'https://grow.almabetter.com/masters-ds/practice/quiz-assessments',
  },
  {
    title: (
      <p>
        Cheat
        <br />
        Sheet
      </p>
    ),
    description:
      'Essential cheat sheets for data science and software engineering with AI courses. Quick, clear, and helpful.',
    link: 'cheat-sheet',
  },
];

const sliderList = {
  rightSide: ['Microsoft.png', 'Netflix.png', 'Uber.png', 'Linkedin.png'],
  leftSide: ['Meta.png', 'Airbnb.png', 'Amazon.png', 'Microsoft.png'],
};

const BytesHeadCard = ({ queryString }) => {
  return (
    <a
      href={`https://grow.almabetter.com/data-science/practice/coding-problems?${queryString}`}
      target="_blank"
    >
      <div className="group flex h-full flex-col gap-y-4 rounded-lg bg-[#1A1A1A] px-6 pt-8">
        <span className="text-[14px] leading-5 text-red">GET JOB READY!!</span>
        <h5 className="w-[87%] font-satoshi-bold text-4xl group-hover:text-red">
          Practice Free Coding Problems
        </h5>
        <p className="font-satoshi-medium text-lg">
          Learn to code like the top 1%. Practice hundreds of coding problems and assessments asked
          in top tech companies like Meta, Amazon, Google etc.
        </p>
        <div className="mt-10 flex flex-col gap-y-12 rounded-t-lg bg-[#2A2A2A] py-10">
          <Marquee direction={'right'} speed={120}>
            {sliderList.rightSide.map((name, index) => (
              <img src={`/assets/${name}`} className="mx-4 h-9 object-contain" key={index + 1} />
            ))}
          </Marquee>
          <Marquee direction={'left'} speed={120}>
            {sliderList.leftSide.map((name, index) => (
              <img src={`/assets/${name}`} className="mx-4 h-9 object-contain" key={index + 1} />
            ))}
          </Marquee>
        </div>
      </div>
    </a>
  );
};

const BytesCard = ({ title, description, comingSoon, link, index, ...props }) => {
  return (
    <div
      {...props}
      className={`bytes-card-bg group relative flex h-fit w-auto cursor-pointer flex-col justify-between gap-y-6 overflow-hidden rounded-xl p-6 font-satoshi-medium md:h-60`}
    >
      <p className="font-satoshi-medium text-[14px] leading-5 tracking-normal text-white lg:pr-8 lg:text-sm">
        {description}
      </p>
      <h5 className="whitespace-break-spaces flex items-center justify-between font-satoshi-regular text-2xl group-hover:text-red">
        {title}
        <FiArrowUpRight />
      </h5>
    </div>
  );
};

const BytesHomePage = ({ tutorials, articlesData }) => {
  const { query } = useRouter();
  const queryString = getUtmQueryString(query);
  return (
    <>
      <SiteHeader
        title="AlmaBetter Bytes: Tutorials, Articles, Videos, InterviewPrep"
        desc="Explore the most indemand tutorials, articles, interview preparations and free courses about data science and software engineering with AI from AlmaBetter Bytes platform"
      />
      <section className="child:mb-16 [&>div]:ml-6 [&>div]:md:!mx-28 [&>div:not(:first-child)]:md:!mb-20">
        <section className=" relativeflex flex-col items-center justify-center bg-black  py-16 font-satoshi-medium text-white">
          <div className="absolute inset-0 hidden h-full w-full md:block">
            <img
              src="/assets/refer-and-earn/Dot grid.png"
              alt=""
              className="h-full w-full"
              draggable={false}
            />
          </div>
          <div className="z-1 relative">
            <div className="flex flex-col items-center justify-center">
              <h1 className="lg:center w-3/5 text-center font-satoshi-bold text-2xl tracking-normal lg:text-5xl ">
                Learn better each day with <span className="text-red">AlmaBetter</span> Bytes
              </h1>

              <div className="mt-8 flex w-[87%] flex-col items-center gap-6 md:flex-row lg:mt-16">
                <BytesHeadCard queryString={queryString} />
                <div className="grid h-full gap-6 md:grid-cols-3 md:grid-rows-2">
                  {cardData?.map((item, index) => {
                    const httpLink = item.link?.startsWith('http');

                    return httpLink ? (
                      <a
                        href={
                          item.link?.includes('youtube') ? item.link : `${item.link}?${queryString}`
                        }
                        target="_blank"
                      >
                        <BytesCard {...item} key={item.title} index={index} />
                      </a>
                    ) : (
                      <Link
                        href={`/bytes/${item.link}${queryString ? `?${queryString}` : ''}`}
                        as={`/bytes/${item.link}${queryString ? `?${queryString}` : ''}`}
                        passHref
                      >
                        <a>
                          <BytesCard {...item} key={item.title} index={index} />
                        </a>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
        <BytesArticles articleCard data={articlesData?.data} />
        <BytesTutorials relatedTutorials data={tutorials?.data} />
        <section>
          <EventBanner />
        </section>
        <BytesFooter />
      </section>
    </>
  );
};
export default BytesHomePage;

export async function getStaticProps() {
  try {
    const apiBaseUrl = getBytesStrapiUrl();
    const articles = await getArticles({ page: 1, size: 9 });
    const tutorials = await axios.get(
      `${apiBaseUrl}/api/tutorials?populate=*&pagination[page]=1&pagination[pageSize]=9&sort[0]=publishedAt:desc`
    );
    return {
      props: {
        articlesData: articles?.data || {},
        tutorials: tutorials?.data || {},
      },
      revalidate: 1800,
    };
  } catch (error) {
    return { props: { error: JSON.stringify(error) } };
  }
}
